// AdminEmailsPage — pestaña "Correos" (F2 motor de correos por situación).
// Muestra segmentos vivos (clasificados por esapi al momento), stats por
// plantilla, lista de dueños con su historial y acciones mute/baja.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { CORREOS_TABS } from '../pestanas';
import { useConfirmDialog } from '../../../components/common/ConfirmDialog';
import {
  Mail, RefreshCw, LifeBuoy, Eye, MousePointerClick, BellOff, Ban,
  ChevronDown, ChevronUp, Moon, Sun, CheckCircle2, Activity,
} from 'lucide-react';
import {
  fetchEmailOverview, fetchEmailOwners, fetchEmailOwnerDetail, emailOwnerAction,
  EmailOverview, EmailOwner, EmailLogEntry,
} from '../../../services/emailEngineService';

const SEGMENT_LABELS: Record<string, string> = {
  pagador_activo: 'Pagador activo',
  pagador_en_riesgo: 'Pagador en riesgo',
  power_free: 'Power free',
  churn_con_volumen: 'Churn con volumen',
  activo_chico: 'Activo chico',
  enfriandose: 'Enfriándose',
  abandono_con_datos: 'Abandono con datos',
  configuro_sin_operar: 'Configuró sin operar',
  reciente_sin_arrancar: 'Reciente sin arrancar',
  fantasma: 'Fantasma',
};

const fmtDate = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const fmtDateTime = (ms: number | null) =>
  ms ? new Date(ms).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    sent: 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300',
    shadow: 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300',
    test: 'bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300',
  };
  const labels: Record<string, string> = { sent: 'Enviado', shadow: 'Shadow', test: 'Prueba' };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || 'bg-surface-2 text-content-2'}`}>
      {labels[status] || status}
    </span>
  );
};

const OwnerHistory: React.FC<{ uid: string }> = ({ uid }) => {
  const [logs, setLogs] = useState<EmailLogEntry[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEmailOwnerDetail(uid)
      .then((d) => setLogs(d.logs))
      .catch((e) => setError(e.message));
  }, [uid]);

  if (error) return <p className="text-sm text-red-600 dark:text-red-400 px-4 py-3">{error}</p>;
  if (!logs) return <p className="text-sm text-content-3 px-4 py-3 animate-pulse">Cargando historial…</p>;
  if (!logs.length) return <p className="text-sm text-content-3 px-4 py-3">Sin correos registrados todavía.</p>;

  return (
    <div className="px-4 py-3 space-y-2">
      {logs.map((l) => (
        <div key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm border border-line rounded-lg px-3 py-2 bg-surface">
          <StatusBadge status={l.status} />
          <span className="font-medium text-content">{SEGMENT_LABELS[l.template] || l.template}</span>
          <span className="text-content-3">{fmtDateTime(l.sentAt)}</span>
          {l.subject && <span className="text-content-2 truncate max-w-xs" title={l.subject}>«{l.subject}»</span>}
          <span className="flex items-center text-content-2" title={l.openedAt ? `Abierto ${fmtDateTime(l.openedAt)} (${l.opens}×)` : 'Sin abrir'}>
            <Eye className={`h-3.5 w-3.5 mr-1 ${l.openedAt ? 'text-green-600 dark:text-green-400' : 'text-content-3'}`} />
            {l.openedAt ? `${l.opens}×` : '—'}
          </span>
          <span className="flex items-center text-content-2" title={l.clicks.map((c) => c.url).join('\n') || 'Sin clics'}>
            <MousePointerClick className={`h-3.5 w-3.5 mr-1 ${l.clicks.length ? 'text-green-600 dark:text-green-400' : 'text-content-3'}`} />
            {l.clicks.length || '—'}
          </span>
          {l.status === 'sent' && (
            <span
              className={`flex items-center text-xs font-medium ${l.activityAfter ? 'text-green-700 dark:text-green-400' : 'text-content-3'}`}
              title="¿El dueño tuvo actividad en su negocio después de este correo?"
            >
              <Activity className="h-3.5 w-3.5 mr-1" />
              {l.activityAfter ? 'Actividad posterior' : 'Sin actividad posterior'}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const AdminEmailsPage: React.FC = () => {
  const { confirm, dialog } = useConfirmDialog();
  const { currentUser } = useAuth();
  const [overview, setOverview] = useState<EmailOverview | null>(null);
  const [owners, setOwners] = useState<EmailOwner[]>([]);
  const [segmentFilter, setSegmentFilter] = useState<string>('');
  const [expanded, setExpanded] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const [ov, ow] = await Promise.all([fetchEmailOverview(refresh), fetchEmailOwners()]);
      setOverview(ov);
      setOwners(ow.owners);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error cargando datos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const list = segmentFilter ? owners.filter((o) => o.segment === segmentFilter) : owners;
    // Primero los que ya tienen correos, luego por clientes
    return [...list].sort((a, b) => (b.log?.sent || 0) + (b.log?.shadow || 0) - ((a.log?.sent || 0) + (a.log?.shadow || 0)) || b.clients - a.clients);
  }, [owners, segmentFilter]);

  const runAction = async (o: EmailOwner, action: 'mute' | 'unmute' | 'optout' | 'optin') => {
    const labels = { mute: 'silenciar 60 días a', unmute: 'reactivar a', optout: 'dar de BAJA de correos a', optin: 'reactivar correos para' };
    const ok = await confirm({
      title: `¿${labels[action].charAt(0).toUpperCase()}${labels[action].slice(1)} ${o.businessName || o.email}?`,
      message: 'Se aplica de inmediato sobre sus correos.',
      confirmLabel: 'Aplicar',
    });
    if (!ok) return;
    setBusy(o.uid + action);
    try {
      await emailOwnerAction(o.uid, action, action === 'mute' ? 60 : undefined);
      setOwners((prev) => prev.map((x) => x.uid === o.uid
        ? {
            ...x,
            optOut: action === 'optout' ? true : action === 'optin' ? false : x.optOut,
            muteUntil: action === 'mute' ? Date.now() + 60 * 864e5 : action === 'unmute' ? null : x.muteUntil,
          }
        : x));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error aplicando la acción.');
    } finally {
      setBusy('');
    }
  };

  // Señales (badges) de un dueño — compartido entre la tabla (desktop) y las tarjetas (móvil).
  const ownerSignals = (o: EmailOwner) => (
    <div className="flex items-center gap-1.5 flex-wrap">
      {o.optOut && (
        <span className="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300" title="Se dio de baja: jamás recibirá correos">
          <Ban className="h-3 w-3 mr-1" />Baja
        </span>
      )}
      {!o.optOut && o.muteUntil && o.muteUntil > Date.now() && (
        <span className="flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300" title={`Pausado hasta ${fmtDate(o.muteUntil)}`}>
          <BellOff className="h-3 w-3 mr-1" />Pausa
        </span>
      )}
      {!o.optOut && o.log && o.log.sent > 0 && o.log.opened > 0 && (
        <span className="flex items-center text-green-600 dark:text-green-400" title="Ha abierto correos">
          <CheckCircle2 className="h-4 w-4" />
        </span>
      )}
    </div>
  );

  // Acciones (mute/baja/expandir) de un dueño — compartido entre desktop y móvil.
  const ownerActions = (o: EmailOwner) => (
    <div className="flex items-center justify-end gap-1">
      {!o.optOut && (o.muteUntil && o.muteUntil > Date.now() ? (
        <button
          onClick={() => runAction(o, 'unmute')}
          disabled={busy === o.uid + 'unmute'}
          className="p-1.5 rounded-md text-content-3 hover:text-accent hover:bg-accent/10 transition-colors"
          title="Quitar pausa"
        >
          <Sun className="h-4 w-4" />
        </button>
      ) : (
        <button
          onClick={() => runAction(o, 'mute')}
          disabled={busy === o.uid + 'mute'}
          className="p-1.5 rounded-md text-content-3 hover:text-accent hover:bg-accent/10 transition-colors"
          title="Pausar 60 días"
        >
          <Moon className="h-4 w-4" />
        </button>
      ))}
      <button
        onClick={() => runAction(o, o.optOut ? 'optin' : 'optout')}
        disabled={busy === o.uid + (o.optOut ? 'optin' : 'optout')}
        className="p-1.5 rounded-md text-content-3 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 transition-colors"
        title={o.optOut ? 'Reactivar correos' : 'Dar de baja de correos'}
      >
        <Ban className="h-4 w-4" />
      </button>
      <button
        onClick={() => setExpanded(expanded === o.uid ? '' : o.uid)}
        className="p-1.5 rounded-md text-content-3 hover:text-accent hover:bg-accent/10 transition-colors"
        title="Ver historial de correos"
      >
        {expanded === o.uid ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
    </div>
  );

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md shadow border border-red-200 animate-fade-in-up">
          <div className="flex">
            <LifeBuoy className="h-5 w-5 text-red-400 flex-shrink-0" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Acceso restringido</h3>
              <p className="mt-2 text-sm text-red-700 dark:text-red-300">Solo los administradores pueden ver el panel de correos.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const tpl = overview?.templates || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <Mail className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
              Correos
            </h1>
            <p className="text-sm text-content-2 mt-1">
              Seguimiento automático a dueños según su uso real. Cada correo registra apertura, clics y si hubo actividad después.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {overview && (
              <span
                className={`px-3 py-1.5 text-xs font-bold rounded-full uppercase tracking-wide ${
                  overview.mode === 'live'
                    ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'
                    : 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300'
                }`}
                title={overview.mode === 'live' ? 'El motor envía correos reales' : 'Modo sombra: el motor decide y registra, pero NO envía'}
              >
                {overview.mode === 'live' ? '● En vivo' : '◐ Shadow (no envía)'}
              </span>
            )}
            <button
              onClick={() => load(true)}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm font-medium rounded-md bg-accent-solid hover:bg-accent-solid/90 text-white disabled:bg-line disabled:text-content-3 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </div>

        <PestanasModulo tabs={CORREOS_TABS} activa="motor" testid="correos-tabs" />

        {error && (
          <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-md p-3 text-sm text-red-700 dark:text-red-300">{error}</div>
        )}

        {/* Resumen del motor */}
        {overview?.engineMeta && (
          <p className="text-xs text-content-3">
            Último barrido: {overview.engineMeta.lastRunDay} — {overview.engineMeta.lastSummary.candidates} candidatos,{' '}
            {overview.engineMeta.lastSummary.sent} {overview.mode === 'live' ? 'enviados' : 'registrados (shadow)'},{' '}
            {overview.engineMeta.lastSummary.errors} errores · {overview.owners} dueños · {overview.optOuts} bajas
          </p>
        )}

        {/* Chips de segmento */}
        {overview && (
          <div className="flex flex-wrap gap-2 animate-fade-in-up">
            <button
              onClick={() => setSegmentFilter('')}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                !segmentFilter ? 'bg-accent/15 border-accent/40 text-accent' : 'bg-surface border-line text-content-2 hover:bg-surface-2'
              }`}
            >
              Todos ({overview.owners})
            </button>
            {Object.entries(overview.segmentCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([seg, count]) => (
                <button
                  key={seg}
                  onClick={() => setSegmentFilter(segmentFilter === seg ? '' : seg)}
                  title={overview.segments[seg]}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    segmentFilter === seg ? 'bg-accent/15 border-accent/40 text-accent' : 'bg-surface border-line text-content-2 hover:bg-surface-2'
                  }`}
                >
                  {SEGMENT_LABELS[seg] || seg} ({count})
                </button>
              ))}
          </div>
        )}

        {/* Stats por plantilla — tabla en desktop, tarjetas apiladas en móvil */}
        {Object.keys(tpl).length > 0 && (
          <div className="hidden md:block overflow-x-auto border border-line rounded-lg animate-fade-in-up">
            <table className="min-w-full divide-y divide-line">
              <thead className="bg-surface-2">
                <tr>
                  {['Plantilla', 'Enviados', 'Shadow', 'Abiertos', 'Con clic'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-line">
                {Object.entries(tpl).map(([name, s]) => (
                  <tr key={name}>
                    <td className="px-4 py-3 text-sm font-medium text-content">{SEGMENT_LABELS[name] || name}</td>
                    <td className="px-4 py-3 text-sm text-content-2">{s.sent}</td>
                    <td className="px-4 py-3 text-sm text-content-2">{s.shadow}</td>
                    <td className="px-4 py-3 text-sm text-content-2">
                      {s.opened}{s.sent > 0 && <span className="text-content-3"> ({Math.round((s.opened / s.sent) * 100)}%)</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-content-2">
                      {s.clicked}{s.sent > 0 && <span className="text-content-3"> ({Math.round((s.clicked / s.sent) * 100)}%)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats por plantilla — tarjetas (móvil) */}
        {Object.keys(tpl).length > 0 && (
          <div className="md:hidden space-y-2 animate-fade-in-up">
            {Object.entries(tpl).map(([name, s]) => (
              <div key={name} className="border border-line rounded-lg bg-surface p-3">
                <div className="text-sm font-medium text-content mb-2 break-words">{SEGMENT_LABELS[name] || name}</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  <div className="flex justify-between"><span className="text-xs text-content-3">Enviados</span><span className="text-sm text-content-2">{s.sent}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-content-3">Shadow</span><span className="text-sm text-content-2">{s.shadow}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-content-3">Abiertos</span><span className="text-sm text-content-2">{s.opened}{s.sent > 0 && <span className="text-content-3"> ({Math.round((s.opened / s.sent) * 100)}%)</span>}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-content-3">Con clic</span><span className="text-sm text-content-2">{s.clicked}{s.sent > 0 && <span className="text-content-3"> ({Math.round((s.clicked / s.sent) * 100)}%)</span>}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabla de dueños (desktop) */}
        <div className="hidden md:block overflow-x-auto border border-line rounded-lg animate-fade-in-up">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-2">
              <tr>
                {['Negocio', 'Segmento', 'Clientes', 'Inactivo', 'Último correo', 'Señales', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-line">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-content-3 animate-pulse">Clasificando dueños…</td></tr>
              )}
              {!loading && filtered.map((o) => (
                <React.Fragment key={o.uid}>
                  <tr className="hover:bg-surface-2 transition-colors duration-150 align-middle">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-content">{o.businessName || '(sin nombre)'}</div>
                      <div className="text-xs text-content-3">{o.email}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-content-2 border border-line" title={overview?.segments[o.segment]}>
                        {SEGMENT_LABELS[o.segment] || o.segment}
                      </span>
                      {['pro', 'premium'].includes(o.plan) && (
                        <span className="ml-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 uppercase">{o.plan}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-content-2">{o.clients}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-content-2">{o.idleDays !== null ? `${o.idleDays}d` : 'nunca'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {o.log?.lastSentAt ? (
                        <div className="flex items-center gap-2">
                          <StatusBadge status={o.log.lastStatus || 'sent'} />
                          <span className="text-content-2">{SEGMENT_LABELS[o.log.lastTemplate || ''] || o.log.lastTemplate}</span>
                          <span className="text-content-3 text-xs">{fmtDate(o.log.lastSentAt)}</span>
                        </div>
                      ) : (
                        <span className="text-content-3">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ownerSignals(o)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      {ownerActions(o)}
                    </td>
                  </tr>
                  {expanded === o.uid && (
                    <tr>
                      <td colSpan={7} className="bg-surface-2/60">
                        <OwnerHistory uid={o.uid} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!loading && !filtered.length && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-content-3">Sin dueños en este segmento.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Tarjetas de dueños (móvil) */}
        <div className="md:hidden space-y-3 animate-fade-in-up">
          {loading && (
            <p className="py-8 text-center text-sm text-content-3 animate-pulse">Clasificando dueños…</p>
          )}
          {!loading && !filtered.length && (
            <p className="py-8 text-center text-sm text-content-3">Sin dueños en este segmento.</p>
          )}
          {!loading && filtered.map((o) => (
            <div key={o.uid} className="border border-line rounded-lg bg-surface overflow-hidden">
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-content truncate">{o.businessName || '(sin nombre)'}</div>
                    <div className="text-xs text-content-3 truncate">{o.email}</div>
                  </div>
                  <div className="flex-shrink-0">{ownerActions(o)}</div>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-surface-2 text-content-2 border border-line" title={overview?.segments[o.segment]}>
                    {SEGMENT_LABELS[o.segment] || o.segment}
                  </span>
                  {['pro', 'premium'].includes(o.plan) && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 uppercase">{o.plan}</span>
                  )}
                  {ownerSignals(o)}
                </div>

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-2">
                  <span>Clientes: <span className="font-medium text-content">{o.clients}</span></span>
                  <span>Inactivo: <span className="font-medium text-content">{o.idleDays !== null ? `${o.idleDays}d` : 'nunca'}</span></span>
                </div>

                <div className="mt-2 text-xs">
                  {o.log?.lastSentAt ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-content-3">Último correo:</span>
                      <StatusBadge status={o.log.lastStatus || 'sent'} />
                      <span className="text-content-2">{SEGMENT_LABELS[o.log.lastTemplate || ''] || o.log.lastTemplate}</span>
                      <span className="text-content-3">{fmtDate(o.log.lastSentAt)}</span>
                    </div>
                  ) : (
                    <span className="text-content-3">Sin correos todavía</span>
                  )}
                </div>
              </div>
              {expanded === o.uid && (
                <div className="border-t border-line bg-surface-2/60">
                  <OwnerHistory uid={o.uid} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {dialog}
    </Layout>
  );
};

export default AdminEmailsPage;
